import { Edit as EditIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material';
import React, { useCallback } from 'react';

import { UPDATE_INVOICE_DETAIL_NOTES, UPDATE_INVOICE_NOTES } from '../../../queries';
import { convertUnixMsToDateInTimezone, dayjs, formatEnumValueForDisplay } from '../../../utils';
import EditDueDateModal from './editDueDateModal';
import { EditInvoiceNotesModal } from './editInvoiceNotesModal';
import { InvoiceDetailsGridItem } from './invoiceDetailsItem';

function formatInvoiceDate(date: string) {
	return dayjs.utc(date).format('M/D/YYYY');
}

export const InvoiceDetailsAccordion = ({
	id,
	readableId,
	accountId,
	type,
	parkingPeriodMonth,
	parkingPeriodYear,
	createdAt,
	subscriptionName,
	dueOn,
	notes,
	detailNotes,
	refetch,
	facilityTimezone,
	isDraft,
	billingDate,
	subscriptionId,
	facilityId,
	setShowDialog,
	setShowSnackbar,
	AccessControlComponent = null,
}) => {
	const handleModalClose = (shouldRefetch) => {
		if (shouldRefetch) {
			refetch();
		}
		setShowDialog(null);
	};
	const setModal = (notesToShow, mutationToUse, headerText, validator = null) =>
		setShowDialog(
			<EditInvoiceNotesModal
				{...{
					id,
					mutationToUse,
					existingNotes: notesToShow,
					handleClose: handleModalClose,
					headerText,
					validator,
				}}
				setShowSnackbar={setShowSnackbar}
			/>,
		);
	const validateInvoiceDetailNotes = (notesToSave: string) => {
		if (notesToSave.length > 500) {
			return 'Invoice Detail Notes cannot be more than 500 characters';
		}
		return null;
	};

	const openEditDueDateModal = useCallback(() => {
		setShowDialog(
			<EditDueDateModal
				invoiceId={id}
				originalDueDate={dueOn}
				handleClose={handleModalClose}
				setShowSnackbar={setShowSnackbar}
			/>,
		);
	}, [dueOn, handleModalClose, id, setShowDialog, setShowSnackbar]);

	const subscriptionInfoLink =
		process.env.NEXT_PUBLIC_APP_NAME === 'OWNER_PORTAL'
			? `/subscription-detail/${subscriptionId}?facilityId=${facilityId}&accountId=${accountId}`
			: `/subscription-info/?subscription=${subscriptionId}&facility=${facilityId}&accountId=${accountId}`;

	return (
		<Accordion defaultExpanded>
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				aria-controls='invoice-details-content'
				id='invoice-details-header'
			>
				<Typography component='span' style={{ fontWeight: 'bold' }}>
					Details
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Box display='flex' flexDirection='row' flexWrap='wrap' justifyContent='start'>
					<InvoiceDetailsGridItem label='Invoice Type' value={formatEnumValueForDisplay(type)} />
					<InvoiceDetailsGridItem label='Invoice Number' value={readableId} />
					<InvoiceDetailsGridItem label='Account ID' value={`AC-${accountId}`} link={subscriptionInfoLink} />
					<InvoiceDetailsGridItem
						label='Parking Period'
						value={
							parkingPeriodMonth && parkingPeriodYear ? `${parkingPeriodMonth} ${parkingPeriodYear}` : '-'
						}
					/>
					<InvoiceDetailsGridItem
						label='Invoice Date'
						value={
							billingDate
								? convertUnixMsToDateInTimezone(billingDate, 'UTC')
								: convertUnixMsToDateInTimezone(createdAt, facilityTimezone)
						}
					/>
					<InvoiceDetailsGridItem label='Subscription Name' value={subscriptionName} />
					<InvoiceDetailsGridItem
						label='Due Date'
						value={formatInvoiceDate(dueOn)}
						icon={
							!!AccessControlComponent && (
								<AccessControlComponent
									componentName='InvoiceDueDateEditIcon'
									AuthComponent={<EditIcon onClick={openEditDueDateModal} />}
								/>
							)
						}
					/>
					<InvoiceDetailsGridItem label='Is Draft' value={isDraft} />
					<div>
						<Button
							variant='contained'
							style={{ marginRight: '3px' }}
							onClick={() => setModal(notes, UPDATE_INVOICE_NOTES, 'General Notes (Internal Use Only)')}
							data-cy='general-notes-button'
						>
							General Notes (Internal Use Only)
						</Button>
						<Button
							variant='contained'
							style={{ marginRight: '3px' }}
							onClick={() =>
								setModal(
									detailNotes,
									UPDATE_INVOICE_DETAIL_NOTES,
									'Invoice Detail Notes (Displayed on Invoice)',
									validateInvoiceDetailNotes,
								)
							}
							data-cy='detail-notes-button'
						>
							Invoice Detail Notes (Displayed on Invoice)
						</Button>
					</div>
				</Box>
			</AccordionDetails>
		</Accordion>
	);
};
